/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * GuideFigure - Reusable component for guide screenshots with placeholder fallback
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react'
import ReactDOM from 'react-dom'
import PropTypes from 'prop-types'
import { Image, X } from 'lucide-react'

const GuideFigure = ({
  src,
  alt,
  caption,
  number,
  compact = false,
  focusArea = 'center',
  onClick
}) => {
  const [imageError, setImageError] = useState(false)
  const [imageLoaded, setImageLoaded] = useState(false)
  const [showLightbox, setShowLightbox] = useState(false)
  const closeButtonRef = useRef(null)
  const triggerRef = useRef(null)

  // Try to load image from public assets
  const imagePath = `/assets/help/getting-started/${src}`

  // Map focus area to object-position
  const focusPositionMap = {
    top: 'object-top',
    center: 'object-center',
    bottom: 'object-bottom'
  }
  const objectPosition = focusPositionMap[focusArea] || 'object-center'

  const openLightbox = useCallback(() => {
    if (!imageError) {
      setShowLightbox(true)
    }
  }, [imageError])

  const closeLightbox = useCallback((e) => {
    if (e) e.stopPropagation()
    setShowLightbox(false)
    // Restore focus to trigger element
    triggerRef.current?.focus()
  }, [])

  const handleClick = useCallback(() => {
    if (onClick) {
      onClick()
    } else if (compact) {
      openLightbox()
    }
  }, [onClick, compact, openLightbox])

  const handleKeyDown = useCallback(
    (e) => {
      if (compact && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault()
        handleClick()
      }
    },
    [compact, handleClick]
  )

  // Handle ESC key to close lightbox
  const handleLightboxKeyDown = useCallback(
    (e) => {
      if (e.key === 'Escape') {
        closeLightbox(e)
      }
    },
    [closeLightbox]
  )

  // Focus management for lightbox
  useEffect(() => {
    if (showLightbox && closeButtonRef.current) {
      closeButtonRef.current.focus()
    }
  }, [showLightbox])

  // Prevent body scroll when lightbox is open
  useEffect(() => {
    if (showLightbox) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [showLightbox])

  const lightboxContent = showLightbox && (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Enlarged view of ${alt}`}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
      onClick={closeLightbox}
      onKeyDown={handleLightboxKeyDown}
    >
      <button
        ref={closeButtonRef}
        onClick={closeLightbox}
        aria-label="Close lightbox"
        className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-black"
      >
        <X className="w-6 h-6 text-white" aria-hidden="true" />
      </button>
      <img
        src={imagePath}
        alt={alt}
        className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      />
      {caption && (
        <p className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-sm bg-black/50 px-4 py-2 rounded-lg">
          {caption}
        </p>
      )}
    </div>
  )

  return (
    <>
      <figure className={compact ? 'my-0' : 'my-8'}>
        <div
          ref={triggerRef}
          role={compact ? 'button' : undefined}
          tabIndex={compact ? 0 : undefined}
          aria-label={compact ? `View enlarged: ${alt}` : undefined}
          className={`relative w-full rounded-xl overflow-hidden border-2 border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 ${
            compact
              ? 'shadow-md hover:shadow-xl hover:scale-[1.02] transition-all duration-300 cursor-pointer group focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2'
              : 'shadow-lg'
          } ${compact ? 'h-48 sm:h-56' : ''}`}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
        >
          {!imageError ? (
            <>
              <img
                src={imagePath}
                alt={alt}
                className={`w-full transition-all duration-300 ${
                  imageLoaded ? 'opacity-100' : 'opacity-0'
                } ${compact ? `h-full object-cover ${objectPosition}` : 'h-auto'} ${
                  compact ? 'group-hover:brightness-105' : ''
                }`}
                onLoad={() => setImageLoaded(true)}
                onError={() => setImageError(true)}
              />
              {!imageLoaded && !imageError && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-gray-800 animate-pulse">
                  <div className="text-center">
                    <Image
                      className="w-12 h-12 text-gray-400 dark:text-gray-600 mx-auto mb-2"
                      aria-hidden="true"
                    />
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Loading screenshot...
                    </p>
                  </div>
                </div>
              )}
              {compact && imageLoaded && (
                <div
                  className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 group-focus:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-4"
                  aria-hidden="true"
                >
                  <span className="text-white text-sm font-medium">Click to enlarge</span>
                </div>
              )}
            </>
          ) : (
            <div
              className={`w-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-900 ${compact ? 'h-full p-4' : 'min-h-[300px] p-8'}`}
            >
              <div className="text-center max-w-md">
                <div
                  className={`${compact ? 'w-10 h-10' : 'w-16 h-16'} bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center mx-auto mb-2`}
                >
                  <Image
                    className={`${compact ? 'w-5 h-5' : 'w-8 h-8'} text-primary-600 dark:text-primary-400`}
                    aria-hidden="true"
                  />
                </div>
                <h3
                  className={`${compact ? 'text-sm' : 'text-lg'} font-semibold text-gray-900 dark:text-gray-100 mb-1`}
                >
                  Screenshot {number || 'Placeholder'}
                </h3>
                {!compact && (
                  <>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                      <span className="font-medium">{alt}</span>
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-500 mt-2">
                      Expected file:{' '}
                      <code className="bg-gray-200 dark:bg-gray-700 px-2 py-1 rounded">{src}</code>
                    </p>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
        {caption && (
          <figcaption
            className={`mt-2 text-center text-gray-600 dark:text-gray-400 ${compact ? 'text-xs' : 'text-sm italic'}`}
          >
            {caption}
          </figcaption>
        )}
      </figure>

      {/* Lightbox Modal - rendered via portal to avoid z-index issues */}
      {typeof document !== 'undefined' && ReactDOM.createPortal(lightboxContent, document.body)}
    </>
  )
}

GuideFigure.propTypes = {
  src: PropTypes.string.isRequired,
  alt: PropTypes.string.isRequired,
  caption: PropTypes.string,
  number: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  compact: PropTypes.bool,
  focusArea: PropTypes.oneOf(['top', 'center', 'bottom']),
  onClick: PropTypes.func
}

export default GuideFigure
